import { useNavigate, useSearch } from "@tanstack/react-router";
import { TableRowSelection } from "antd/es/table/interface";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils.ts";
import { Key, useMemo, useRef } from "react";
import { useEffect, useState } from "react";

interface Search {
    selectedRows?: string[];
}

const stringFilter = (item: string | undefined): item is string => Boolean(item);

export const useReportRowSelectors = <T extends object>(
    data: T[] | undefined,
    callback: (record: T[][number]) => string | undefined,
    metric: string
) => {
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [hasRenderedWithDefinedData, setHasRenderedWithDefinedData] = useState(false);
    const hasManualSelection = useRef(false);

    const navigate = useNavigate();

    const { selectedRows: paramSelectedRows }: Search = useSearch({ strict: false });
    const [isOn, setIsOn] = useState<boolean>(true);

    const { defaultSelectionBase, filteredSelection } = useMemo(() => {
        const entitiesLookup = new Map<string, number>();

        for (const entry of data || []) {
            const entity = callback(entry);

            if (!entity) {
                continue;
            }

            const mapMatch = entitiesLookup.get(entity) || 0;
            entitiesLookup.set(entity, mapMatch + GeneralGraphUtils.resolveMetricValueToNumber(entry[metric as keyof T]));
        }

        const sortedEntities = Array.from(entitiesLookup.entries())
            .sort(([, a], [, b]) => b - a)
            .map(([key]) => key);

        const topTen = sortedEntities.slice(0, 10);
        const defaultSelectionBase = data ? Array.from(new Set(data.map(callback).filter(stringFilter))) : [];
        const filteredSelection = defaultSelectionBase.filter((item) => topTen.includes(item));

        return { defaultSelectionBase, filteredSelection };
    }, [data, callback, metric]);

    useEffect(() => {
        if (!hasRenderedWithDefinedData && data !== undefined) {
            const hasUrlSelection = paramSelectedRows && paramSelectedRows.length > 0;
            if (hasUrlSelection) {
                hasManualSelection.current = true;
            }
            setSelectedRows(
                hasUrlSelection
                    ? paramSelectedRows.filter((item) => defaultSelectionBase.includes(item))
                    : filteredSelection
            );

            setHasRenderedWithDefinedData(true);
        }
    }, [data, defaultSelectionBase, filteredSelection, hasRenderedWithDefinedData]); // paramSelectedRows not in deps arr on purpose, we only want the data as they are on mount

    // Update selection when metric/sort changes, but only if user hasn't made manual selections
    useEffect(() => {
        if (hasRenderedWithDefinedData && !hasManualSelection.current) {
            setSelectedRows(filteredSelection);
        }
    }, [filteredSelection, hasRenderedWithDefinedData]);

    const onSelectChange = (newSelectedRowKeys: Key[]) => {
        hasManualSelection.current = true;
        const stringKeys = newSelectedRowKeys.map(String);
        navigate({
            search: (prev) =>
                ({
                    ...prev,
                    selectedRows: stringKeys.length ? stringKeys : undefined,
                }) as never,
        });
        setSelectedRows(stringKeys);
    };

    const handleSetIsOn = (value: boolean) => {
        setIsOn(value);

        if (!value) {
            hasManualSelection.current = false;
            setSelectedRows(filteredSelection);

            navigate({
                search: (prev) =>
                    ({
                        ...prev,
                        selectedRows: undefined,
                    }) as never,
            });
        }
    };

    const rowSelection: TableRowSelection<T[][number]> = {
        selectedRowKeys: selectedRows,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            disabled: selectedRows.length >= 10 && !selectedRows.includes(callback(record as T) || ""),
        }),
    };

    return {
        rowSelection,
        selectedRows,
        isSelectionOn: isOn,
        setIsSelectionOn: handleSetIsOn,
        defaultSelection: filteredSelection,
    };
};
